import React, { Suspense, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Home, User, NotebookPen, Menu, Calendar, Archive, Bell, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveLayoutTitle } from '@/lib/layoutTitle';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FirstVisitOnboarding } from '@/components/FirstVisitOnboarding';

export const Layout: React.FC = () => {
  const navigate = useAppNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const headerTitle = resolveLayoutTitle(location.pathname);

  const navItems = [
    { icon: Home, label: 'Недели', path: '/weeks' },
    { icon: Calendar, label: 'Трекер', path: '/tracker' },
    { icon: NotebookPen, label: 'Журнал самоисследования', path: '/self-enquiry/history' },
    { icon: Archive, label: 'Архив', path: '/archive' },
    { icon: Bell, label: 'Уведомления', path: '/settings/notifications' },
    { icon: User, label: 'Профиль', path: '/profile' },
    { icon: Info, label: 'О проекте', path: '/about' },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const isActive = (path: string) => {
    if (path === '/weeks') {
      return location.pathname === '/weeks' || location.pathname === '/';
    }
    if (path === '/tracker') {
      return location.pathname === '/tracker';
    }
    if (path === '/profile') {
      return location.pathname.startsWith('/profile');
    }
    if (path === '/self-enquiry/history') {
      return location.pathname.startsWith('/self-enquiry');
    }
    if (path === '/archive') {
      return location.pathname.startsWith('/archive');
    }
    if (path === '/settings/notifications') {
      return location.pathname.startsWith('/settings/notifications');
    }
    if (path === '/about') {
      return location.pathname.startsWith('/about');
    }
    return false;
  };

  const navTestIds: Record<string, string> = {
    '/weeks': 'layout-nav-weeks',
    '/tracker': 'layout-nav-tracker',
    '/archive': 'layout-nav-archive',
    '/profile': 'layout-nav-profile',
    '/self-enquiry/history': 'layout-nav-self-enquiry',
    '/settings/notifications': 'layout-nav-notifications',
    '/about': 'layout-nav-about',
  };

  return (
    <div data-testid="app-layout">
      <FirstVisitOnboarding />
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 border-b border-border/35',
          'bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/75',
        )}
      >
        <div className="pt-[env(safe-area-inset-top,0px)]">
          <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
            <h1 className="min-w-0 flex-1 truncate text-lg font-bold tracking-tight text-foreground">
              {headerTitle}
            </h1>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-full border-border/50 bg-card shadow-card hover:bg-accent hover:text-accent-foreground hover:shadow-card-hover"
                  aria-label="Открыть меню"
                  data-testid="layout-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[280px] sm:w-[320px]"
                data-testid="layout-nav-sheet"
              >
                <SheetHeader>
                  <SheetTitle className="text-left text-xl font-semibold text-foreground">
                    РОбот
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 space-y-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        type="button"
                        data-testid={navTestIds[item.path]}
                        onClick={() => handleNavigate(item.path)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left',
                          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          active
                            ? 'bg-accent text-accent-foreground font-semibold'
                            : 'text-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Icon className={cn('h-5 w-5 shrink-0', active && 'text-foreground')} />
                        <span className="text-base">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="min-h-[100dvh] min-w-0 max-w-full overflow-x-clip pt-[calc(3.5rem+env(safe-area-inset-top,0px))]">
        <Suspense
          fallback={
            <div className="container mx-auto flex min-h-[40vh] max-w-3xl items-center justify-center px-4 py-10">
              <LoadingSpinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};
